import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
} from '@mui/material';
import { useWarbandStore } from '../../stores/warbandStore';
import { useStargraveCustomStore } from '../../stores/stargraveCustomStore';
import { StargraveEquipment } from '../../types';
import { CustomContentManager } from '../shared/CustomContentManager';

interface FormState {
  name: string;
  category: StargraveEquipment['category'];
  gearSlots: string;
  damageModifier: string;
  maxRange: string;
  moveModifier: string;
  armourModifier: string;
  notes: string;
}

const emptyForm = (): FormState => ({
  name: '',
  category: 'gear',
  gearSlots: '1',
  damageModifier: '',
  maxRange: '',
  moveModifier: '',
  armourModifier: '',
  notes: '',
});

function equipmentToForm(item: StargraveEquipment): FormState {
  return {
    name: item.name,
    category: item.category,
    gearSlots: String(item.gearSlots),
    damageModifier: item.damageModifier !== undefined ? String(item.damageModifier) : '',
    maxRange: item.maxRange !== undefined ? String(item.maxRange) : '',
    moveModifier: item.moveModifier !== undefined ? String(item.moveModifier) : '',
    armourModifier: item.armourModifier !== undefined ? String(item.armourModifier) : '',
    notes: item.notes ?? '',
  };
}

function parseOptionalNum(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
}

const EquipmentFormFields: React.FC<{
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}> = ({ form, setForm }) => {
  const setField = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <TextField label="Name" value={form.name} onChange={(e) => setField('name', e.target.value)} fullWidth required size="small" />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Category</InputLabel>
          <Select label="Category" value={form.category} onChange={(e) => setField('category', e.target.value as StargraveEquipment['category'])}>
            <MenuItem value="weapon">Weapon</MenuItem>
            <MenuItem value="armour">Armour</MenuItem>
            <MenuItem value="gear">Gear</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <TextField label="Gear Slots" type="number" value={form.gearSlots} onChange={(e) => setField('gearSlots', e.target.value)} fullWidth size="small" />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <TextField label="Damage Modifier" type="number" value={form.damageModifier} onChange={(e) => setField('damageModifier', e.target.value)} fullWidth size="small" placeholder="None" />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <TextField label="Max Range" type="number" value={form.maxRange} onChange={(e) => setField('maxRange', e.target.value)} fullWidth size="small" placeholder="None" />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <TextField label="Move Modifier" type="number" value={form.moveModifier} onChange={(e) => setField('moveModifier', e.target.value)} fullWidth size="small" placeholder="None" />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <TextField label="Armour Modifier" type="number" value={form.armourModifier} onChange={(e) => setField('armourModifier', e.target.value)} fullWidth size="small" placeholder="None" />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField label="Notes" value={form.notes} onChange={(e) => setField('notes', e.target.value)} fullWidth multiline minRows={2} size="small" />
      </Grid>
    </Grid>
  );
};

export const ManageStargraveEquipmentModal: React.FC = () => {
  const { ui, closeCustomEquipmentModal } = useWarbandStore();
  const customEquipment = useStargraveCustomStore((s) => s.customEquipment);
  const addCustomEquipment = useStargraveCustomStore((s) => s.addCustomEquipment);
  const updateCustomEquipment = useStargraveCustomStore((s) => s.updateCustomEquipment);
  const deleteCustomEquipment = useStargraveCustomStore((s) => s.deleteCustomEquipment);

  return (
    <Dialog open={ui.customEquipmentModalOpen} onClose={closeCustomEquipmentModal} maxWidth="sm" fullWidth
      PaperProps={{ sx: { height: '85vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle>Custom Equipment</DialogTitle>
      <DialogContent dividers sx={{ overflowY: 'auto', flex: 1 }}>
        {ui.customEquipmentModalOpen && <CustomContentManager<StargraveEquipment, FormState>
          itemLabel="Equipment"
          emptyMessage="No custom equipment yet."
          items={customEquipment}
          getItemName={(item) => item.name}
          getItemSecondary={(item) => [
            item.category,
            `${item.gearSlots} slot${item.gearSlots === 1 ? '' : 's'}`,
            item.damageModifier !== undefined ? `DMG ${item.damageModifier >= 0 ? '+' : ''}${item.damageModifier}` : null,
            item.maxRange !== undefined ? `Range ${item.maxRange}"` : null,
            item.armourModifier !== undefined ? `Armour ${item.armourModifier >= 0 ? '+' : ''}${item.armourModifier}` : null,
          ].filter(Boolean).join(' · ')}
          emptyForm={emptyForm}
          itemToForm={equipmentToForm}
          validate={(form) => (form.name.trim() ? null : 'Name is required.')}
          renderFormFields={(form, setForm) => <EquipmentFormFields form={form} setForm={setForm} />}
          onSave={(form, editingId) => {
            const data: Omit<StargraveEquipment, 'id' | 'isCustom'> = {
              name: form.name.trim(),
              category: form.category,
              gearSlots: parseOptionalNum(form.gearSlots) ?? 1,
              damageModifier: parseOptionalNum(form.damageModifier),
              maxRange: parseOptionalNum(form.maxRange),
              moveModifier: parseOptionalNum(form.moveModifier),
              armourModifier: parseOptionalNum(form.armourModifier),
              notes: form.notes.trim() || undefined,
              sourcePage: 0,
            };
            if (editingId) updateCustomEquipment(editingId, data);
            else addCustomEquipment(data);
          }}
          onDelete={deleteCustomEquipment}
        />}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeCustomEquipmentModal}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
